import type { CSSProperties } from 'react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import IconPadlock from '~/vendor/cxy/icons/icons/Padlock';
import clsx from 'clsx';

import { Avatar, AvatarImage } from '../Avatar';
import { Button } from '../Button';

import styles from './styles/OddsButton.module.scss';

export interface OddsButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  bookieColour: string;
  bookieLogo: string;
  bookieName: string;
  flexDirection?: CSSProperties['flexDirection'];
  hideBookieLogo?: boolean;
  label?: string;
  line?: string;
  locked?: boolean;
  odds: number | string;
  selected?: boolean;
  shouldHideLabelOnDesktop?: boolean;
  shouldHideLabelOnMobile?: boolean;
  wrapperClassName?: string;
  wrapperStyles?: CSSProperties;
}

export const OddsButton = forwardRef<HTMLButtonElement, OddsButtonProps>(
  (
    {
      bookieColour,
      bookieLogo,
      bookieName,
      className = '',
      flexDirection = 'column',
      hideBookieLogo = false,
      label,
      line,
      locked = false,
      odds,
      selected,
      shouldHideLabelOnDesktop = false,
      shouldHideLabelOnMobile = false,
      wrapperClassName = '',
      wrapperStyles = {},
      ...props
    },
    ref,
  ) => {
    const buttonComponent = (
      <Button
        className={clsx(styles.oddsButton, className)}
        disabled={props.disabled}
        ref={ref}
        variant={selected ? 'secondary' : 'default'}
        {...props}
        pressed={selected}
        title={bookieName}
      >
        {locked ? (
          <IconPadlock className={styles.lockedIcon} />
        ) : (
          <>
            {!hideBookieLogo && (
              <Avatar size="small" style={{ backgroundColor: bookieColour }}>
                <AvatarImage alt={`${bookieName} logo`} src={bookieLogo} />
              </Avatar>
            )}
            <div className={clsx(styles.oddsContainer, styles[`direction-${flexDirection}`])}>
              {line && <span className={styles.line}>{line}</span>}
              <span className={clsx(styles.odds, { [styles.withLine]: line })}>{odds}</span>
            </div>
          </>
        )}
      </Button>
    );

    if (label) {
      return (
        <div
          className={clsx(
            styles.oddsButtonWrapper,
            {
              [styles.shouldHideLabelOnDesktop]: shouldHideLabelOnDesktop,
              [styles.shouldHideLabelOnMobile]: shouldHideLabelOnMobile,
            },
            wrapperClassName,
          )}
          data-cxy-name="odds-button"
          style={wrapperStyles}
        >
          <p>{label}</p>
          {buttonComponent}
        </div>
      );
    }

    return buttonComponent;
  },
);

OddsButton.displayName = 'OddsButton';
